/**
 * useMercureReconnect — Handles SSE reconnection when user permissions change.
 *
 * When a permission update is detected via the Mercure subscription,
 * this hook:
 * 1. Asks the backend to re-issue the Mercure subscriber cookie
 * 2. Triggers the Mercure client to reconnect
 *
 * Step 1 is not optional. The cookie's payload is the user's list of authorized
 * Mercure topics, computed from their permissions and accessible filiales at the
 * moment it was written, and it lives 24h. `EventSource` sends cookies but not
 * headers, so reconnecting alone re-authorizes with the very same stale topics:
 * a newly granted topic would never deliver, and a revoked one would keep
 * delivering. Refreshing the cookie first is what makes the rotation mean
 * anything — see MercureTokenController.
 *
 * The cookie is HttpOnly and the endpoint answers 204, so no token is ever read
 * in JavaScript: there is nothing here that could be written into the auth store,
 * which is what used to wipe the session payload (userId/refreshToken) and 401
 * every API call.
 */

import api from "@lib/api/client";
import { API_ENDPOINTS } from "@lib/config/constants";
import { usePermissionStore } from "@lib/stores/permissions.store";
import { getMercureClient } from "@lib/utils/mercure";
import { useEffect, useRef } from "react";

const LOG_PREFIX = "[MercureReconnect]";

/**
 * Ask the backend to re-issue the Mercure subscriber cookie.
 *
 * 204 with a Set-Cookie and an empty body: the cookie is the only channel, so
 * there is no token to parse and nothing that could leak into the auth store.
 * Returns whether the cookie was actually refreshed — rotating against a stale
 * cookie would reconnect on the old topics and look like it worked.
 */
async function refreshMercureCookie(): Promise<boolean> {
  try {
    const response = await api.get(API_ENDPOINTS.MERCURE_TOKEN);
    return response.status === 204;
  } catch (error) {
    console.error(`${LOG_PREFIX} Failed to refresh the Mercure cookie:`, error);
    return false;
  }
}

/**
 * Hook that monitors permission changes and triggers Mercure SSE reconnection.
 *
 * Mount this hook inside `AppMercureSubscriber` (or any component that
 * has access to the Mercure context). It subscribes to Zustand permission
 * store changes and re-establishes the SSE connection when needed.
 *
 * @example
 * ```tsx
 * function AppMercureSubscriber({ children }) {
 *   useMercureReconnect();
 *   return children;
 * }
 * ```
 */
export function useMercureReconnect(): void {
  const previousPermissionsHash = useRef<string>("");
  const isInitialMount = useRef(true);

  useEffect(() => {
    // Subscribe to permission store changes (Zustand selector)
    const unsubscribe = usePermissionStore.subscribe((state) => {
      // Build a lightweight hash of permission-relevant state
      const hash = [
        state.permissions.join(","),
        state.hasWildcard ? "1" : "0",
        state.context.filiale_code,
        state.context.accessible_filiales?.join(",") ?? "",
      ].join("|");

      // Skip the initial mount — we only react to *changes*
      if (isInitialMount.current) {
        isInitialMount.current = false;
        previousPermissionsHash.current = hash;
        return;
      }

      // No change → no-op
      if (hash === previousPermissionsHash.current) return;
      previousPermissionsHash.current = hash;

      // Permissions changed → refresh Mercure token and reconnect
      handlePermissionChange();
    });

    return unsubscribe;
  }, []);
}

/**
 * Core reconnection logic: refresh the subscriber cookie, then reconnect SSE.
 */
async function handlePermissionChange(): Promise<void> {
  if (import.meta.env?.DEV) {
    console.log(`${LOG_PREFIX} Permission change detected, refreshing Mercure connection...`);
  }

  // 1. Re-issue the subscriber cookie so it carries the CURRENT topic set.
  const refreshed = await refreshMercureCookie();

  // 2. Trigger SSE reconnection — MercureClient.rotateConnection()
  //    closes the current EventSource and opens a new one, which now
  //    authenticates with the refreshed cookie.
  try {
    const client = getMercureClient();
    if (client && typeof client.rotateConnection === "function") {
      client.rotateConnection();
      if (import.meta.env?.DEV) {
        console.log(
          `${LOG_PREFIX} SSE reconnection triggered (cookie ${refreshed ? "refreshed" : "NOT refreshed"})`,
        );
      }
    } else if (import.meta.env?.DEV) {
      console.warn(
        `${LOG_PREFIX} MercureClient.rotateConnection not available — topics will update on next connect cycle`,
      );
    }
  } catch (error) {
    console.error(`${LOG_PREFIX} Failed to trigger reconnection:`, error);
  }
}

// Re-export for convenience
export default useMercureReconnect;
